import React, { useEffect, useState } from 'react';
import { C, ACCENT } from '../../theme';

const STATUS_VARIANT = { active:'success', pending:'warning', suspended:'danger' };
const PLAN_VARIANT   = { trial:'warning', starter:'info', agency:'info', enterprise:'info' };

function authFetch(url, opts={}) {
  const token = localStorage.getItem('plan_token');
  return fetch(url, { ...opts, headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token, ...(opts.headers||{}) } });
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={{...C.modal, width:560}} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>x</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState('orgs');
  const [orgs, setOrgs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);   // edit modal: org object
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [o, p, s] = await Promise.all([
        authFetch('/api/admin/organizations').then(r=>r.json()),
        authFetch('/api/admin/billing-plans').then(r=>r.json()),
        authFetch('/api/admin/stats').then(r=>r.json()),
      ]);
      setOrgs(Array.isArray(o)?o:[]); 
      setPlans(Array.isArray(p)?p:[]); 
      setStats(s);
    } catch(e) { console.error(e); }
    setLoading(false);
  }
  useEffect(()=>{ load(); },[]);

  async function approveOrg(id) {
    await authFetch('/api/admin/organizations/'+id+'/approve', { method:'POST' });
    load();
  }

  async function suspendOrg(id) {
    if (!window.confirm('Suspend this organization?')) return;
    await authFetch('/api/admin/organizations/'+id+'/suspend', { method:'POST' });
    load();
  }

  async function createOrg() {
    if (!createForm.name?.trim()) return alert('Organization name is required');
    if (!createForm.admin_email?.trim()) return alert('Admin email is required');
    if (!createForm.admin_password?.trim()) return alert('Admin password is required');
    setSaving(true);
    try {
      const r = await authFetch('/api/admin/organizations', { method: 'POST', body: JSON.stringify(createForm) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setCreateOpen(false); setCreateForm({});
      await load();
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  }

  async function saveOrg() {
    setSaving(true);
    try {
      await authFetch('/api/admin/organizations/'+modal.id, { method:'PATCH', body:JSON.stringify(form) });
      await load(); setModal(null);
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  }

  async function savePlan(key) {
    const plan = plans.find(p=>p.key===key);
    if (!plan) return;
    await authFetch('/api/admin/billing-plans/'+key, { method:'PATCH', body:JSON.stringify(plan) });
    load();
  }

  const tabStyle = (t) => ({
    padding:'8px 20px', fontSize:13, fontWeight:600, cursor:'pointer',
    background:'transparent', border:'none',
    borderBottom: tab===t ? '2px solid '+ACCENT : '2px solid transparent',
    color: tab===t ? ACCENT : '#8aa0b8',
  });

  return (
    <div style={C.page}>
      <div style={{ marginBottom:24 }}>
        <h1 style={C.title}>Super Admin <span style={{ fontSize:12, color:'#8aa0b8', fontWeight:400 }}>Platform Management</span></h1>
        {stats && (
          <div style={{ display:'flex', gap:12, marginTop:16, flexWrap:'wrap' }}>
            {[
              { l:'Total Orgs', v:stats.total_orgs },
              { l:'Pending', v:stats.pending_orgs, hi: stats.pending_orgs > 0 },
              { l:'Active', v:stats.active_orgs },
              { l:'Users', v:stats.total_users },
              { l:'Clients', v:stats.total_clients },
              { l:'Plans', v:stats.total_plans },
            ].map(s=>(
              <div key={s.l} style={{ padding:'10px 16px', background: s.hi ? 'rgba(208,128,0,0.08)' : 'rgba(255,255,255,0.75)', border:'1px solid '+(s.hi ? '#d08000' : '#dce8f0'), borderRadius:10, textAlign:'center', minWidth:90 }}>
                <div style={{ fontSize:11, color: s.hi ? '#d08000' : '#8aa0b8', marginBottom:4 }}>{s.l}</div>
                <div style={{ fontSize:22, fontWeight:800, color: s.hi ? '#d08000' : '#1a2a3a' }}>{s.v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display:'flex', borderBottom:'1px solid #dce8f0', marginBottom:20, alignItems:'center' }}>
        <button style={tabStyle('orgs')} onClick={()=>setTab('orgs')}>Organizations</button>
        <button style={tabStyle('plans')} onClick={()=>setTab('plans')}>Billing Plans</button>
        <div style={{flex:1}}/>
        {tab==='orgs' && <button style={{...C.btnPrimary, fontSize:12, padding:'6px 14px', marginBottom:4}} onClick={()=>{setCreateForm({org_type:'agency',plan:'trial',status:'active'});setCreateOpen(true);}}>+ Create Org</button>}
      </div>

      {loading ? <div style={C.empty}>Loading...</div> : tab==='orgs' ? (
        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Organization</th>
              <th style={C.th}>Type</th>
              <th style={C.th}>Status</th>
              <th style={C.th}>Plan</th>
              <th style={C.th}>Users</th>
              <th style={C.th}>Clients</th>
              <th style={C.th}>Registered</th>
              <th style={C.th}>Expires</th>
              <th style={C.th}></th>
            </tr>
          </thead>
          <tbody>
            {orgs.map(o=>(
              <tr key={o.id} style={{...C.tr, cursor:'default'}}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...C.td, fontWeight:700}}>
                  {o.name}
                  {o.billing_email && <div style={{fontSize:11,color:'#8aa0b8'}}>{o.billing_email}</div>}
                </td>
                <td style={C.td}><span style={C.mutedLabel()}>{o.org_type||'agency'}</span></td>
                <td style={C.td}>
                  <span style={C.statusRow()}>
                    <span style={C.statusDot(STATUS_VARIANT[o.status]||'neutral')}/>
                    {o.status}
                  </span>
                </td>
                <td style={C.td}><span style={C.mutedLabel()}>{o.plan||'trial'}</span></td>
                <td style={{...C.td, color:'#6a8aaa'}}>{o.user_count||0} / {o.max_users===-1?'unlimited':o.max_users}</td>
                <td style={{...C.td, color:'#6a8aaa'}}>{o.client_count||0} / {o.max_clients===-1?'unlimited':o.max_clients}</td>
                <td style={{...C.td, color:'#6a8aaa', fontSize:11}}>{o.created_at?new Date(o.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}):'--'}</td>
                <td style={{...C.td, fontSize:11, color:o.plan_expires_at&&new Date(o.plan_expires_at)<new Date()?'#a02020':'#6a8aaa'}}>
                  {o.plan_expires_at?new Date(o.plan_expires_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}):'--'}
                </td>
                <td style={{...C.td, textAlign:'right', whiteSpace:'nowrap'}}>
                  {o.status==='pending' && <button style={{...C.btnPrimary, padding:'4px 10px', fontSize:11, marginRight:6}} onClick={()=>approveOrg(o.id)}>Approve</button>}
                  <button style={{...C.btnSecondary, fontSize:11, marginRight:6}} onClick={()=>{setForm({status:o.status,plan:o.plan||'trial',org_type:o.org_type||'agency',max_users:o.max_users,max_clients:o.max_clients,max_brands:o.max_brands,billing_email:o.billing_email||'',notes:o.notes||'',taxonomy_enabled:!!o.taxonomy_enabled});setModal(o);}}>Edit</button>
                  {o.status==='active' && o.id!==1 && <button style={{...C.btnDanger, fontSize:11}} onClick={()=>suspendOrg(o.id)}>Suspend</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Plan</th>
              <th style={C.th}>Price/mo (EUR)</th>
              <th style={C.th}>Max Users</th>
              <th style={C.th}>Max Clients</th>
              <th style={C.th}>Max Brands</th>
              <th style={C.th}>Trial Days</th>
              <th style={C.th}>Active</th>
              <th style={C.th}></th>
            </tr>
          </thead>
          <tbody>
            {plans.map(p=>(
              <tr key={p.key} style={{...C.tr, cursor:'default'}}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...C.td, fontWeight:700}}>
                  <span style={{ fontWeight: 600, color: '#3a4a5a', fontSize: 12 }}>{p.name}</span>
                </td>
                {['price_monthly','max_users','max_clients','max_brands','trial_days'].map(f=>(
                  <td key={f} style={C.td}>
                    <input type="number" value={p[f]} style={{...C.input, width:80, padding:'4px 8px', fontSize:12}}
                      onChange={e=>setPlans(prev=>prev.map(x=>x.key===p.key?{...x,[f]:+e.target.value}:x))}/>
                  </td>
                ))}
                <td style={C.td}>
                  <input type="checkbox" checked={!!p.active} onChange={e=>setPlans(prev=>prev.map(x=>x.key===p.key?{...x,active:e.target.checked}:x))}/>
                </td>
                <td style={{...C.td, textAlign:'right'}}>
                  <button style={{...C.btnSecondary, fontSize:11}} onClick={()=>savePlan(p.key)}>Save</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createOpen && (
        <Modal title="Create Organization" onClose={()=>setCreateOpen(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <label style={{display:'block', gridColumn:'1/-1'}}>
              <span style={C.label}>Organization Name</span>
              <input value={createForm.name||''} onChange={e=>setCreateForm(p=>({...p,name:e.target.value}))} style={C.input} placeholder="e.g. Acme Media Group"/>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Org Type</span>
              <select value={createForm.org_type||'agency'} onChange={e=>setCreateForm(p=>({...p,org_type:e.target.value}))} style={C.input}>
                <option value="agency">Agency</option>
                <option value="advertiser">Advertiser</option>
              </select>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Plan</span>
              <select value={createForm.plan||'trial'} onChange={e=>setCreateForm(p=>({...p,plan:e.target.value}))} style={C.input}>
                {plans.map(p=><option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Status</span>
              <select value={createForm.status||'active'} onChange={e=>setCreateForm(p=>({...p,status:e.target.value}))} style={C.input}>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
              </select>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Billing Email</span>
              <input value={createForm.billing_email||''} onChange={e=>setCreateForm(p=>({...p,billing_email:e.target.value}))} style={C.input} placeholder="billing@company.com"/>
            </label>
            <label style={{display:'block', gridColumn:'1/-1'}}><span style={{...C.label, color:'#8aa0b8', fontSize:10, letterSpacing:'.08em'}}>ADMIN USER</span></label>
            <label style={{display:'block'}}>
              <span style={C.label}>Admin Email</span>
              <input value={createForm.admin_email||''} onChange={e=>setCreateForm(p=>({...p,admin_email:e.target.value}))} style={C.input} placeholder="admin@company.com"/>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Admin Name</span>
              <input value={createForm.admin_name||''} onChange={e=>setCreateForm(p=>({...p,admin_name:e.target.value}))} style={C.input} placeholder="Full Name"/>
            </label>
            <label style={{display:'block', gridColumn:'1/-1'}}>
              <span style={C.label}>Admin Password</span>
              <input type="password" value={createForm.admin_password||''} onChange={e=>setCreateForm(p=>({...p,admin_password:e.target.value}))} style={C.input} placeholder="Set initial password"/>
            </label>
          </div>
          <label style={{display:'block', marginTop:12, marginBottom:16}}>
            <span style={C.label}>Notes</span>
            <textarea value={createForm.notes||''} onChange={e=>setCreateForm(p=>({...p,notes:e.target.value}))} rows={2} style={{...C.input, resize:'vertical'}}/>
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setCreateOpen(false)}>Cancel</button>
            <button style={C.btnPrimary} onClick={createOrg} disabled={saving}>{saving?'Creating...':'Create'}</button>
          </div>
        </Modal>
      )}

      {modal && (
        <Modal title={'Edit: '+modal.name} onClose={()=>setModal(null)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <label style={{display:'block'}}>
              <span style={C.label}>Status</span>
              <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={C.input}>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Plan</span>
              <select value={form.plan} onChange={e=>setForm(p=>({...p,plan:e.target.value}))} style={C.input}>
                {plans.map(p=><option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Org Type</span>
              <select value={form.org_type} onChange={e=>setForm(p=>({...p,org_type:e.target.value}))} style={C.input}>
                <option value="agency">Agency</option>
                <option value="advertiser">Advertiser</option>
              </select>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Billing Email</span>
              <input value={form.billing_email||''} onChange={e=>setForm(p=>({...p,billing_email:e.target.value}))} style={C.input}/>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Max Users (-1=unlimited)</span>
              <input type="number" value={form.max_users} onChange={e=>setForm(p=>({...p,max_users:+e.target.value}))} style={C.input}/>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Max Clients (-1=unlimited)</span>
              <input type="number" value={form.max_clients} onChange={e=>setForm(p=>({...p,max_clients:+e.target.value}))} style={C.input}/>
            </label>
            <label style={{display:'block'}}>
              <span style={C.label}>Max Brands (-1=unlimited)</span>
              <input type="number" value={form.max_brands} onChange={e=>setForm(p=>({...p,max_brands:+e.target.value}))} style={C.input}/>
            </label>
            <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
              <input type="checkbox" checked={!!form.taxonomy_enabled} onChange={e=>setForm(p=>({...p,taxonomy_enabled:e.target.checked}))}/>
              <span style={C.label}>Taxonomy enabled (Gemius/GDE ZE-code)</span>
            </label>
          </div>
          <label style={{display:'block', marginTop:12, marginBottom:16}}>
            <span style={C.label}>Notes</span>
            <textarea value={form.notes||''} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} style={{...C.input, resize:'vertical'}}/>
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={saveOrg} disabled={saving}>{saving?'Saving...':'Save'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
